// Angular + external
import {
  ChangeDetectorRef,
  ElementRef,
  Injectable,
  Injector,
  OnChanges,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { Observable, Subject } from "rxjs";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

// BCDFX - Globals
// Services
import { ATDLComparatorService } from "../../services/atdl/atdl-comparator.service";
import { ATDLUtilityService } from "../../services/atdl/atdl-utility.service";
import { OELoggerService } from "../../services/basic/oe-logger.service";
// Components

// Models - ICD
import { ICDAuxCheckboxModel } from "../../data-idc/controls/icd-aux-checkbox.model";
import { ICDBaseModel } from "../../data-idc/controls/icd-base.model";
import { ICDCommandBaseModel } from "../../data-idc/coordination/icd-command-base.model";
import { ICDResponseBaseModel } from "../../data-idc/coordination/icd-response-base.model";
import { ICDTransportModel } from "../../data-idc/coordination/icd-transport.model";
// Models - ATDL
import { ATDLInterfaceAuxiliaryModel } from "../../data-atdl/model/atdl-interface-auxiliary.model";
import { ATDLInterfaceControlModel } from "../../data-atdl/model/atdl-interface-control.model";
import { ATDLInterfaceDependencyModel } from "../../data-atdl/model/atdl-interface-dependency.model";
// Enums - ICD Coordination
import { ICDNResponseCommand } from "../../data-idc/enums/icd-response-cmd.enum";
// Enums - ATDL
import { ATDLNControlType } from "../../data-atdl/enums/atdl-control-type.enum";
import { ATDLNOperator } from "../../data-atdl/enums/atdl-operator.enum";
// Enums - ICD
import { ICDNAuxMode } from "../../data-idc/enums/icd-aux-mode.enum";
import { ICDNCommand } from "../../data-idc/enums/icd-command.enum";
// Enums - OE
import { OENEntryType } from "../../data/enums/oe-entry-type.enum";
import { ICNInputControlState } from "../controls/models/bfx-interface-controls.enums";
import { ComponentMixin } from "../../../../shared/base-components/mixins/mixin";
import { DomHandler } from "@bfx/primeng/dom";
import { InputLabelStateValue } from "shared/base-components/mixins/label-state";

export const ICD_BASE_DEFAULT_HOST_RECORD: Record<string, string> = {
  "[class.icd__base__container]": "true",
  "[class.icd__base__container--valid]": "data.state === 0 && !locked && !disabled",
  "[class.icd__base__container--invalid]": "data.state === 1 && !locked && !disabled",
  "[class.icd__base__container--active]": "data.state === 2 && !locked && !disabled",
  "[class.icd__base__container--inactive]": "data.state == 3 && !locked && !disabled",
  "[class.icd__base__container--locked]": "disabled || locked",
};

@Injectable()
export abstract class ICDBase<T> extends ComponentMixin implements OnInit, OnChanges, OnDestroy {
  /**********************************************************************/
  /* DATA */

  // INPUTS
  public icdId: string = null;
  public entryType: OENEntryType = OENEntryType.ALGO;
  ///
  public label: string = "DYN Control"; // change in sub class initialize()
  ///

  // state for visibility - will be used by bfxInputDirective for label states
  private _disabled: boolean = false;
  private _locked: boolean = false;

  public inputLabelState: InputLabelStateValue = "valid";

  public visible: boolean = true;
  //public locked:boolean = false;

  ///
  public inputFocused: boolean = false;

  public dirty: boolean = false;
  ///
  public required: boolean = false;
  ///
  public isInitializing: boolean = true;
  public isLoading: boolean = true;
  ///
  public partFilled: boolean = false;
  public partFillLocked: boolean = false;
  /// DEV TEST
  public control_validation_state: boolean = true;

  // COORDINATION SUBJECT
  public icdResponseSubject: Subject<ICDResponseBaseModel> = new Subject<ICDResponseBaseModel>();

  /* DATA - MODELS */
  // Master DYN ICD model
  public model: ATDLInterfaceControlModel = null;
  // Local Data model - this level only needs{ value:any, state:ICNInputControlState, errorMsg:string, disabled:boolean}
  public data = new ICDBase.Data();

  private static Data = class {
    value: any = null;
    private _state: ICNInputControlState = ICNInputControlState.INACTIVE;
    public get state(): ICNInputControlState {
      return this._state;
    }
    public set state(value: ICNInputControlState) {
      this._state = value;
    }
    boolValue: boolean = null;
    dataset: any[] = null;
    defaultValue: any = null;
    disabled: boolean = false;
    emptyValue: any = null;
    private _errorMsg: string = "";
    public get errorMsg(): string {
      return this._errorMsg;
    }
    public set errorMsg(value: string) {
      this._errorMsg = value;
    }
    filteredItems: any = null;
    initialValue: any = null;
    initValue: any = null;
    item: any = null;
    numValue: any = null;
    optionValue: any = null;
    text: any = null;
    timeValue: any = null;
    tooltip: any = null;
  };

  public auxCheckbox: {
    id: string;
    enabled: boolean;
    locked: boolean;
    permanentLock: boolean; // used when there is a aux checkbox for control that is required, so that checked value can not be changed.
    value: boolean;
    mode: ICDNAuxMode;
  } = {
    id: null,
    enabled: false,
    locked: true,
    permanentLock: false,
    value: false,
    mode: ICDNAuxMode.DISABLED,
  };

  public abstract subData: any;

  public oeLogger: OELoggerService;
  public atdlUtility: ATDLUtilityService;
  public atdlComparator: ATDLComparatorService;
  public cd: ChangeDetectorRef;
  public el: ElementRef;

  /**********************************************************************/
  /* CONSTRUCTOR */
  constructor(injector: Injector) {
    super(injector);
    this.oeLogger = injector.get(OELoggerService);
    this.atdlUtility = injector.get(ATDLUtilityService);
    this.atdlComparator = injector.get(ATDLComparatorService);
    this.cd = injector.get(ChangeDetectorRef);
    this.el = injector.get(ElementRef);

    this.oeLogger = this.oeLogger.for(__dirname);
  }

  /**********************************************************************/
  /* INITIALIZATION */

  protected initialize(): void {
    this.data = Object.assign(this.data, this.subData);
  }

  /**
   * Is component disabled? Changing will also change inputLabelState value
   */
  public get disabled(): boolean {
    return this._disabled;
  }

  public set disabled(value: boolean) {
    if (typeof value === "boolean" && value !== this._disabled) {
      this._disabled = value;

      this.inputLabelState = this._locked || this._disabled ? "disabled" : "valid";
      this.cd.markForCheck();
    }
  }

  /**
   * Is component locked?  Changing will also change inputLabelState value
   */
  public get locked(): boolean {
    return this._locked;
  }
  public set locked(value: boolean) {
    if (typeof value === "boolean" && value !== this._locked) {
      if (this.partFillLocked) {
        this._locked = true;
      } else {
        this._locked = value;
      }

      this.inputLabelState = this._locked || this._disabled ? "disabled" : "valid";
      this.cd.markForCheck();
    }
  }

  /**********************************************************************/
  /* LIFE CYCLE HOOKS */

  ngOnInit() {
    //
  }

  ngOnChanges() {
    //
  }

  ngOnDestroy() {
    //
  }

  /**********************************************************************/
  /* DYN COORDINATION METHODS */

  /* INITIALIZATION */
  public dynInitializeSubscription(): Observable<ICDResponseBaseModel> {
    return this.icdResponseSubject.asObservable();
  }

  public dynInitializeModel(model: ATDLInterfaceControlModel): void {
    let initErrors: boolean = false;
    try {
      if (model instanceof ATDLInterfaceControlModel && typeof model.icdId === "string") {
        this.icdId = model.icdId;
        this.model = model;

        // LABEL
        if (typeof this.model.label === "string") {
          this.label = this.model.label;
          if (this.label.length > 22) {
            this.label = this.label.substring(0, 22) + "..";
          }
        }
        // LOCAL VALIDATION
        if (
          this.model.validity != null &&
          this.model.validity.hasOwnProperty("required") &&
          typeof this.model.validity.required === "boolean"
        ) {
          this.required = this.model.validity.required;
        } else {
          this.required = false;
        }

        // AUX CHECKBOX
        let auxCheckboxInitialized: boolean = false;
        if (this.model.auxiliary instanceof Array && this.model.auxiliary.length > 0) {
          if (this.model.auxiliary.length === 1) {
            let auxControl = this.model.auxiliary[0];
            if (auxControl.refIcdId === this.model.icdId) {
              if (auxControl.icdType === ATDLNControlType.CHECKBOX) {
                // check if control is required -> cannot be optional and required
                if (this.required) {
                  this.auxCheckbox.id = auxControl.icdId;
                  this.auxCheckbox.enabled = true;
                  this.auxCheckbox.locked = false;
                  this.auxCheckbox.permanentLock = true;
                  this.auxCheckbox.value = true;
                  this.auxCheckbox.mode = ICDNAuxMode.REQUIRED;
                } else {
                  this.auxCheckbox.id = auxControl.icdId;
                  this.auxCheckbox.enabled = true;
                  this.auxCheckbox.locked = false;
                  this.auxCheckbox.permanentLock = false;
                  this.auxCheckbox.value =
                    typeof auxControl.initValue === "boolean" ? auxControl.initValue : false;
                  this.auxCheckbox.mode = ICDNAuxMode.OPTIONAL;
                }
                auxControl.value = this.auxCheckbox.value;

                // register aux
                this.dynRespond(ICDNResponseCommand.REGISTER_AUX_REF, auxControl.icdId);
                // set initialized
                auxCheckboxInitialized = true;
                // STATE FLOW AUX DEPENDENCY REFS
                if (
                  auxControl.dependencies instanceof Array &&
                  auxControl.dependencies.length > 0
                ) {
                  for (let dependency of auxControl.dependencies) {
                    if (dependency instanceof ATDLInterfaceDependencyModel) {
                      for (let dependencyId of dependency.fieldDependencies) {
                        if (dependencyId !== this.icdId) {
                          // of not internal dependency
                          this.dynRespond(ICDNResponseCommand.REGISTER_DEPENDENCY, {
                            auxId: auxControl.icdId,
                            depId: dependencyId,
                          });
                        }
                      }
                    } else {
                      logger.out.error(
                        `ERROR:  ${this.constructor.name}.dynInitialize(): [${this.icdId}] IVALID AUX dependency %O`,
                        dependency,
                      );
                    }
                  }
                }
              } else {
                logger.out.error(
                  `ERROR:  ${this.constructor.name}.dynInitialize(): [${
                    this.icdId
                  }] has UNSUPPORTED auxiliary control of type '${
                    ATDLNControlType[auxControl.icdType]
                  }'`,
                );
              }
            } else {
              logger.out.error(
                `ERROR:  ${this.constructor.name}.dynInitialize(): [${this.icdId}] has invalid auxiliary control for '${auxControl.refIcdId}'`,
              );
            }
          } else {
            logger.out.error(
              `ERROR: ${this.constructor.name}.dynInitialize(): [${this.icdId}] has multiple auxiliary controls %O`,
              this.model,
            );
          }
        }
        if (!auxCheckboxInitialized) {
          this.auxCheckbox.enabled = false;
          this.auxCheckbox.id = null;
        }

        // STATE FLOW ICD DEPENDENCY REFS
        if (this.model.dependencies instanceof Array && this.model.dependencies.length > 0) {
          for (let dependency of this.model.dependencies) {
            if (dependency instanceof ATDLInterfaceDependencyModel) {
              for (let dependencyId of dependency.fieldDependencies) {
                if (dependencyId !== this.auxCheckbox.id) {
                  // of not internal dependency
                  this.dynRespond(ICDNResponseCommand.REGISTER_DEPENDENCY, dependencyId);
                }
              }
            } else {
              logger.out.error(
                `ERROR:  ${this.constructor.name}.dynInitialize(): [${this.icdId}] INVALID ICD dependency %O`,
                dependency,
              );
            }
          }
        }

        // ICD Specific Initialization
        initErrors = initErrors || this.initializeICDSpecificModelProperties();

        // Adjust control for aux init value
        if (auxCheckboxInitialized) {
          let dynResponses = this.processICDInternalStateFlow(new Map());
          this.handleDynResponses(dynResponses);
        }

        if (!initErrors) {
          this.dynRespond(ICDNResponseCommand.INITIALIZATION_COMPLETE);
        }
      } else {
        initErrors = true;
      }
    } catch (ex) {
      logger.out.error(
        `ERROR: ${this.constructor.name}.dynInitialize(): ICD [${this.icdId}] Exception => %O`,
        ex,
      );
      initErrors = true;
    }
    // inform about initialization errors
    if (initErrors) {
      setTimeout(() => {
        this.dynRespond(ICDNResponseCommand.INITIALIZATION_ERROR);
      }, 0);
    }
  }

  /* PANEL COMMAND HANDLER */
  public dynCommand(command: ICDCommandBaseModel): void {
    try {
      this.cd.markForCheck();
      if (command instanceof ICDCommandBaseModel) {
        if (command.icdId === this.icdId) {
          // just dev-test logging
          if (command.data != null) {
            this.outputExtendedLogging(
              `ICD: [${this.icdId}] Received => Command: ${ICDNCommand[command.cmd]} | Data: %O`,
              command.data,
            );
          } else {
            this.outputExtendedLogging(
              `ICD: [${this.icdId}] Received => Command: ${ICDNCommand[command.cmd]}`,
            );
          }

          switch (command.cmd) {
            case ICDNCommand.LOCK:
              this.lock();
              break;
            case ICDNCommand.UNLOCK:
              this.unlock();
              break;
            case ICDNCommand.RESET:
              this.reset();
              break;
            case ICDNCommand.RESET_AND_INIT:
              this.isInitializing = true;
              this.resetAndReInit(
                command.data instanceof ATDLInterfaceControlModel ? command.data : null,
              );
              break;
            case ICDNCommand.LOAD:
              this.handleLoad(command.data);
              break;
            case ICDNCommand.VALIDATE:
              this.validate();
              break;
            case ICDNCommand.FOCUS_INPUT:
              this.focusInput();
              break;
            case ICDNCommand.INVALID_INPUT:
              this.handleInvalidInputUpdate(command.data);
              break;
            /***************************/
            case ICDNCommand.UPDATE_VALIDATION:
              this.handleValidationUpdate(command.data);
              break;
            case ICDNCommand.UPDATE_ENTRY_TYPE:
              this.handleEntryTypeUpdate(command.data);
              break;
            case ICDNCommand.UPDATE_LABEL:
              this.label = command.data;
              break;
            case ICDNCommand.UPDATE_AUX_MODEL:
              this.handleAuxModelChange(command.data);
              break;
            case ICDNCommand.UPDATE_CONSTRAINT:
              this.handleConstraintUpdate(command.data);
              break;

            case ICDNCommand.ATDL_CONTROL_VALIDATION:
              if (typeof command.data === "boolean") {
                this.control_validation_state = command.data;
              }
              break;
            case ICDNCommand.UPDATE_PART_FILL_STATE:
              this.handlePartFillUpdate(command.data);
              break;
            /***************************/
            case ICDNCommand.ENABLE:
              this.disabled = false;
              break;
            case ICDNCommand.DISABLE:
              this.disabled = true;
              break;
            /***************************/
            case ICDNCommand.LOADING_START:
              this.isInitializing = false;
              this.isLoading = true;
              break;
            case ICDNCommand.LOADING_END:
              this.isLoading = false;
              break;
            case ICDNCommand.INITIALIZING_START:
              this.isInitializing = true;
              break;
            case ICDNCommand.INITIALIZING_END:
              this.isInitializing = false;
              if (command.data != null && command.data === ICDNCommand.LOADING_START) {
                this.isLoading = true;
              } else {
                this.handleInitializingEnd();
              }
              break;
            case ICDNCommand.PUSH_VALUE:
              this.pushCurrentValue();
              break;
            case ICDNCommand.SCROLL_IN_VIEW:
              this.handleScrollInView(command.data);
              break;
            default:
              logger.out.error(
                `ERROR: ${this.constructor.name}.dynCommand(): [${
                  this.icdId
                }] received unhandled command ${command.cmd} (${ICDNCommand[command.cmd]})`,
              );
              break;
          }
        } else {
          logger.out.error(
            `ERROR: ${this.constructor.name}.dynCommand(): [${this.icdId}] received a command intended for '${command.icdId}'`,
          );
        }
      } else {
        logger.out.error(
          `ERROR: ${this.constructor.name}.dynCommand(): [${this.icdId}] received a command with invalid format %O`,
          command,
        );
      }
    } catch (ex) {
      logger.out.error(
        `ERROR: ${this.constructor.name}.dynCommand(): [${this.icdId}] Exception => %O`,
        ex,
      );
    }
  }

  handleScrollInView(data) {
    let container = data?.container;

    if (container != null) {
      DomHandler.scrollInView(container, this.el.nativeElement);
    }
  }

  public dynRespond(cmd: ICDNResponseCommand, data?: any): void {
    try {
      if (cmd != null) {
        let response = null;
        switch (cmd) {
          case ICDNResponseCommand.UPDATE_VALUE:
            if (data != null && !this.atdlUtility.equals(data, this.data.value)) {
              logger.out.error(
                `ERROR:  ${this.constructor.name}.dynRespond(): [${this.icdId}] Local and global value mismatch`,
              );
            }
            response = new ICDResponseBaseModel(this.icdId, cmd, data);
            break;
          case ICDNResponseCommand.UPDATE_VISIBILITY:
            if (typeof data !== "boolean") {
              logger.out.error(
                `ERROR:  ${this.constructor.name}.dynRespond(): ICD [${this.icdId}] wrong 'data' value on UPDATE_VISIBILITY `,
              );
            } else {
              response = new ICDResponseBaseModel(this.icdId, cmd, data);
            }
            break;
          case ICDNResponseCommand.RESET_ACK:
          case ICDNResponseCommand.PERFORM_VALIDATION:
          case ICDNResponseCommand.REGISTER_AUX_REF:
          case ICDNResponseCommand.REGISTER_DEPENDENCY:
          case ICDNResponseCommand.INITIALIZATION_COMPLETE:
            response = new ICDResponseBaseModel(this.icdId, cmd, data);
            break;
          case ICDNResponseCommand.LOAD_COMPLETE:
            response = new ICDResponseBaseModel(this.icdId, cmd);
            break;
          case ICDNResponseCommand.INITIALIZATION_ERROR:
            this.disabled = true;
            this.data.errorMsg = "Initialization fail";
            response = new ICDResponseBaseModel(this.icdId, cmd, data);
            break;
          case ICDNResponseCommand.REQUEST_RATE:
            response = new ICDResponseBaseModel(this.icdId, cmd, data);
            break;
          default:
            logger.out.error(
              `ERROR:  ${this.constructor.name}.dynRespond(): [${this.icdId}] unhandled response command ${cmd}`,
            );
            break;
        }
        // send response
        if (response instanceof ICDResponseBaseModel) {
          this.icdResponseSubject.next(response);
        } else {
          logger.out.error(
            `ERROR:  ${this.constructor.name}.dynRespond(): [${this.icdId}] Response command model construction failed!`,
          );
        }
      } else {
        logger.out.error(
          `ERROR:  ${this.constructor.name}.dynRespond(): [${this.icdId}] Invalid response command`,
        );
      }
    } catch (ex) {
      logger.out.error(
        `ERROR:  ${this.constructor.name}.dynRespond(): [${this.icdId}] Exception => %O`,
        ex,
      );
    }
  }

  /**********************************************************************/
  /* DYN - HANDLERS */
  protected handleInitializingEnd(): void {
    this.updateValue();
    this.validate();
    this.dynRespond(ICDNResponseCommand.UPDATE_VALUE, this.data.value);
  }

  protected handlePartFillUpdate(partFilled: boolean): void {
    if (typeof partFilled === "boolean") {
      this.cd.markForCheck();
      this.partFilled = partFilled;
      if (this.model.disableOnPartFill && this.partFilled) {
        this.partFillLocked = true;
        this.locked = true;
      } else {
        this.partFillLocked = false;
      }
    }
  }

  protected handleEntryTypeUpdate(entryType: OENEntryType): void {
    try {
      if (entryType != null && entryType in ICNInputControlState) {
        this.cd.markForCheck();
        this.entryType = entryType;
      } else {
        logger.out.error(
          `ERROR:  ${this.constructor.name}.dynCommand(): [${this.icdId}] Invalid data on UPDATE_ENTRY_TYPE change! dump: %O`,
          entryType,
        );
      }
    } catch (ex) {
      logger.out.error(
        `ERROR:  ${this.constructor.name}.handleEntryTypeUpdate(): [${this.icdId}] Exception => %O`,
        ex,
      );
    }
  }

  protected handleInvalidInputUpdate(data: any): void {
    // data
    try {
      if (data instanceof ICDTransportModel && this.icdId === data.icdId) {
        this.cd.markForCheck();
        this.data.state = ICNInputControlState.INVALID;
        this.data.errorMsg = data.errorMsg;
      } else {
        logger.out.error(
          `ERROR: ${this.constructor.name}.dynCommand(): [${this.icdId}] Invalid data on UPDATE_EXTERNAL_STATE! Data dump: %O`,
          data,
        );
      }
    } catch (ex) {
      logger.out.error(
        `ERROR: ${this.constructor.name}.handleExternalStateUpdate(): [${this.icdId}] Exception => %O`,
        ex,
      );
    }
  }

  protected handleLoad(data: any): void {
    try {
      if (data != null) {
        if (data.value != null) {
          if (data.propagate != null && typeof data.propagate === "boolean") {
            this.load(data.value, data.propagate);
          } else {
            this.load(data.value, false);
          }
        } else {
          // load null
          logger.out.error(
            `ERROR:  ${this.constructor.name}.handleLoad(): [${this.icdId}] Invalid data (NOT NULL) on LOAD command! data dump: %O`,
            data,
          );
        }
      } else {
        logger.out.error(
          `ERROR:  ${this.constructor.name}.handleLoad(): [${this.icdId}] Invalid data (NULL) on LOAD command`,
        );
      }
    } catch (ex) {
      logger.out.error(
        `ERROR:  ${this.constructor.name}.handleLoad(): [${this.icdId}] Exception => %O`,
        ex,
      );
    }
  }

  protected handleAuxModelChange(newAuxModel: ICDAuxCheckboxModel): void {
    try {
      if (newAuxModel instanceof ICDAuxCheckboxModel) {
        this.cd.markForCheck();
        this.auxCheckbox.enabled = newAuxModel.enabled;
        this.auxCheckbox.locked = newAuxModel.mode === ICDNAuxMode.DISABLED;
        this.auxCheckbox.value = newAuxModel.value;
        this.auxCheckbox.mode = newAuxModel.mode;
        if (this.model?.auxiliary?.length === 1) {
          // Stick value back into model so available to validator without needing component access
          let auxControl = this.model.auxiliary[0];
          auxControl.value = this.auxCheckbox.value;
        }
      }
    } catch (ex) {
      logger.out.error(
        `ERROR: ${this.constructor.name}.handleControlModeChange(): [${this.icdId}] Exception => %O`,
        ex,
      );
    }
  }

  protected handleValidationUpdate(model: ATDLInterfaceControlModel): void {
    try {
      if (model instanceof ATDLInterfaceControlModel) {
        // model is synced, this is just a check
        this.applyValidationToControl();
      } else {
        logger.out.error(
          `ERROR: ${this.constructor.name}.dynCommand(): [${this.icdId}] Invalid data on UPDATE_VALIDATION change! dump: %O`,
          model,
        );
      }
    } catch (ex) {
      logger.out.error(
        `ERROR: ${this.constructor.name}.handleValidationUpdate(): [${this.icdId}] Exception => %O`,
        ex,
      );
    }
  }

  protected handleDynResponses(dynResponses: Map<ICDNResponseCommand, any>) {
    for (let [cmd, data] of dynResponses.entries()) {
      this.dynRespond(cmd, data);
    }
  }

  /**********************************************************************/
  /* STATE FLOW RULES - DEPENDENCY HANDLERS  */

  /* ICD EXTERNAL => Processing for State-flow rules that depend on external ICD value */
  protected processExternalStateFlowDependency(
    data: any,
    dynResponses: Map<ICDNResponseCommand, any>,
  ): Map<ICDNResponseCommand, any> {
    if (
      data instanceof Object &&
      data.hasOwnProperty("value") &&
      data.hasOwnProperty("name") &&
      typeof data.name === "string"
    ) {
      let unhandledDependency: boolean = true;

      this.cd.markForCheck();

      // ICD has External Dependency
      if (this.model.dependencies instanceof Array && this.model.dependencies.length > 0) {
        for (let dependency of this.model.dependencies) {
          if (
            dependency instanceof ATDLInterfaceDependencyModel &&
            dependency.hasFieldDependency(data.name)
          ) {
            // to be removed - just a check
            if (dependency.fieldOwner !== this.model.icdId) {
              logger.out.error(
                `ERROR: ${this.constructor.name}.handleConstraintUpdate(): ICD [${this.icdId}]: Invalid dependency reference ${dependency.fieldOwner}`,
              );
            }
            // that dependency is this dependency
            dynResponses = this.evaluateAndApplyICDStateFlowAction(dependency, data, dynResponses);
            unhandledDependency = false;
          }
        }
      }
      // AUX has External Dependency
      let auxiliaryControl =
        this.model.auxiliary != null
          ? this.model.auxiliary.find((item) => item.icdId === this.auxCheckbox.id)
          : null;
      if (
        unhandledDependency &&
        auxiliaryControl instanceof ATDLInterfaceAuxiliaryModel &&
        auxiliaryControl.dependencies instanceof Array &&
        auxiliaryControl.dependencies.length > 0
      ) {
        for (let dependency of this.model.dependencies) {
          if (
            dependency instanceof ATDLInterfaceDependencyModel &&
            dependency.hasFieldDependency(data.name)
          ) {
            this.evaluateAndApplyAuxStateFlowAction(dependency, data, dynResponses);
            unhandledDependency = false;
          }
        }
      }

      if (unhandledDependency) {
        logger.out.error(
          `ERROR: ${this.constructor.name}.handleConstraintUpdate(): ICD [${this.icdId}]: Unhandled constraint '${data.name}' Data dump: %O`,
          data,
        );
      }
    } else {
      logger.out.error(
        `ERROR: ${this.constructor.name}.processStateFlowDependency(): ICD [${this.icdId}]: Invalid constraint data! dump: %O`,
        data,
      );
    }
    return dynResponses;
  }

  /* ICD INTERNAL (AUX dependent) => Processing for State-flow rules defined in this ICD that depend on the AUX ic value */
  protected processICDInternalStateFlow(
    dynResponses: Map<ICDNResponseCommand, any>,
  ): Map<ICDNResponseCommand, any> {
    if (this.auxCheckbox.enabled && typeof this.auxCheckbox.id === "string") {
      let auxDependencyRules: Array<ATDLInterfaceDependencyModel> = this.model.dependencies.filter(
        (item) => item.hasFieldDependency(this.auxCheckbox.id),
      );
      if (auxDependencyRules instanceof Array && auxDependencyRules.length > 0) {
        for (let auxDependencyRule of auxDependencyRules) {
          if (auxDependencyRule instanceof ATDLInterfaceDependencyModel) {
            this.evaluateAndApplyICDStateFlowAction(
              auxDependencyRule,
              {
                name: this.auxCheckbox.id,
                value: this.auxCheckbox.value,
              },
              dynResponses,
            );
          } else {
            logger.out.error(
              `ERROR: ${this.constructor.name}.processICDAuxStateFlow(): INVALID Dependency rule `,
            );
          }
        }
      } else {
        logger.out.error(
          `ERROR: ${this.constructor.name}.processICDAuxStateFlow(): Dependency rule for AUX checkbox missing`,
        );
      }
    }
    return dynResponses;
  }

  /* AUX (depends on ICD value) => State flow rules defined for the aux checkbox */
  protected processAuxStateFlow(
    dynResponses: Map<ICDNResponseCommand, any>,
  ): Map<ICDNResponseCommand, any> {
    if (this.auxCheckbox.enabled) {
      if (this.model.auxiliary instanceof Array && this.model.auxiliary.length === 1) {
        let auxiliaryModel = this.model.auxiliary[0];
        if (
          auxiliaryModel instanceof ATDLInterfaceAuxiliaryModel &&
          auxiliaryModel.dependencies instanceof Array &&
          auxiliaryModel.dependencies.length > 0
        ) {
          let auxDependencyRules: Array<ATDLInterfaceDependencyModel> =
            auxiliaryModel.dependencies.filter((item) => item.hasFieldDependency(this.icdId));
          if (auxDependencyRules instanceof Array && auxDependencyRules.length > 0) {
            for (let auxDependencyRule of auxDependencyRules) {
              if (auxDependencyRule instanceof ATDLInterfaceDependencyModel) {
                this.evaluateAndApplyAuxStateFlowAction(
                  auxDependencyRule,
                  {
                    name: this.icdId,
                    value: this.data.value,
                  },
                  dynResponses,
                );
              } else {
                logger.out.error(
                  `ERROR: ${this.constructor.name}.processInternalAuxStateFlow(): INVALID Dependency rule %O`,
                  auxDependencyRule,
                );
              }
            }
          }
        } /* else{
                    logger.out.error(`ERROR: ${this.constructor.name}.processInternalAuxStateFlow(): [${this.icdId}] Invalid auxiliary control model %O`, this.model);
                } */
      } else if (this.model.auxiliary instanceof Array && this.model.auxiliary.length > 1) {
        logger.out.error(
          `ERROR: ${this.constructor.name}.processInternalAuxStateFlow(): [${this.icdId}] has multiple auxiliary controls %O`,
          this.model,
        );
      } else {
        logger.out.error(
          `ERROR: ${this.constructor.name}.processInternalAuxStateFlow(): [${this.icdId}] function triggered when no auxiliary controls %O`,
          this.model,
        );
      }
    }
    return dynResponses;
  }
  /**********************************************************************/
  /* STATE FLOW RULES - IDC Application  */

  protected evaluateAndApplyICDStateFlowAction(
    dependency: ATDLInterfaceDependencyModel,
    data: any,
    dynResponses: Map<ICDNResponseCommand, any>,
  ) {
    if (
      dependency instanceof ATDLInterfaceDependencyModel &&
      data instanceof Object &&
      data.hasOwnProperty("value") &&
      data.hasOwnProperty("name") &&
      dependency.hasFieldDependency(data.name)
    ) {
      if (dependency?.logicGroups?.length == 1 && typeof data.testEditRuleCondition == "function") {
        const applicabilityOutcome = data.testEditRuleCondition(dependency.logicGroups[0]);
        dynResponses = this.applyDependencyRule(dependency, applicabilityOutcome, dynResponses);
      } else {
        try {
          let value2: string = null;
          if (!this.atdlUtility.isSingleParamOperator(dependency.operator)) {
            value2 = dependency.value;
          }
          let applicabilityOutcome: boolean = this.evaluateStateFlowApplicability(
            data.value,
            dependency.operator,
            value2,
          );
          dynResponses = this.applyDependencyRule(dependency, applicabilityOutcome, dynResponses);
        } catch (ex) {
          logger.out.error(
            `ERROR: ${this.constructor.name}.evaluateAndApplyICDStateFlowAction(): Exception ${ex}`,
          );
        }
      }
    } else {
      logger.out.error(
        `ERROR: ${this.constructor.name}.evaluateAndApplyICDStateFlowAction(): ICD [${this.icdId}]: Invalid parameters`,
      );
    }
    return dynResponses;
  }

  /**
   * Apply the states associated with the specified rule, given that the validity check of the rule (either operator or logicOperator)
   * has produce the specified outcome
   * @param dependency
   * @param applicabilityOutcome
   */
  public applyDependencyRule(
    dependency: ATDLInterfaceDependencyModel,
    applicabilityOutcome: boolean,
    dynResponses: Map<ICDNResponseCommand, any>,
  ): Map<ICDNResponseCommand, any> {
    let hasReversal: boolean =
      dependency.action.value === "{NULL}" ||
      typeof dependency.action.enabled === "boolean" ||
      typeof dependency.action.visible === "boolean";
    let applyReversal: boolean = hasReversal && !applicabilityOutcome;
    if (!this.partFillLocked && (applicabilityOutcome || hasReversal)) {
      // visible
      let oldValueVisible: boolean = this.visible;
      if (
        typeof dependency.action.visible === "boolean" &&
        (dependency.action.visible !== this.visible ||
          (applyReversal && dependency.action.visible === this.visible))
      ) {
        this.visible = applyReversal ? !dependency.action.visible : dependency.action.visible;
        if (oldValueVisible !== this.visible) {
          // visibility change
          dynResponses.set(ICDNResponseCommand.UPDATE_VISIBILITY, this.visible);
          // local change
          this.updateValue();
          this.validate();
          dynResponses.set(ICDNResponseCommand.UPDATE_VALUE, this.data.value);
        }
      }
      // enabled
      let oldValueDisabled: boolean = this.data.disabled;
      if (typeof dependency.action.enabled === "boolean") {
        // && (dependency.action.enabled === this.data.disabled || (applyReversal && dependency.action.enabled === this.data.disabled))){
        this.data.disabled = applyReversal ? dependency.action.enabled : !dependency.action.enabled;
        if (!this.auxCheckbox.enabled) {
          // if no checkbox apply to entire component
          this.disabled = this.data.disabled;
        }
        if (oldValueDisabled !== this.data.disabled) {
          // there was a change in 'disability'
          this.updateValue();
          this.validate();
          dynResponses.set(ICDNResponseCommand.UPDATE_VALUE, this.data.value);
        }
      }

      // value change
      if (dependency.action.value != null) {
        if (dependency.action.value === "{NULL}") {
          // Remove reference to model in case this state rule assigned a NULL value to the control
          let nullingDependencyIndex: number =
            this.model.nullingDependencyModels.indexOf(dependency);
          if (nullingDependencyIndex >= 0) {
            this.model.nullingDependencyModels.splice(nullingDependencyIndex, 1);
          }

          if (applicabilityOutcome) {
            this.model.nullingDependencyModels.push(dependency);
          }

          if (
            this.data.value != null &&
            typeof this.data.value === "object" &&
            this.data.value instanceof ICDBaseModel
          ) {
            this.data.value.nulledByDependency = this.model.nullingDependencyModels.length > 0;
          }

          dynResponses.set(ICDNResponseCommand.UPDATE_VALUE, this.data.value);
        } else if (typeof dependency.action.value === "string") {
          if (applicabilityOutcome) {
            // For values, we only update when rule is matched
            this.setAndUpdateValue(dependency.action.value);
            this.validate();
            dynResponses.set(ICDNResponseCommand.UPDATE_VALUE, this.data.value);
          }
        } else {
          logger.out.error(
            `ERROR: ${this.constructor.name}.evaluateAndApplyICDStateFlowAction(): Invalid dependency value on update value action`,
          );
        }
      }
    }

    return dynResponses;
  }

  // NOTE: Dependency actions reversal not implemented for aux since it's a checkbox so no {NULL} value
  // NOTE 2: If permanentLock is true, do nothing
  protected evaluateAndApplyAuxStateFlowAction(
    dependency: ATDLInterfaceDependencyModel,
    data: any,
    dynResponses: Map<ICDNResponseCommand, any>,
  ): Map<ICDNResponseCommand, any> {
    if (!this.auxCheckbox.permanentLock) {
      if (
        dependency instanceof ATDLInterfaceDependencyModel &&
        data instanceof Object &&
        data.hasOwnProperty("value") &&
        data.hasOwnProperty("name") &&
        dependency.hasFieldDependency(data.name)
      ) {
        try {
          let value1 = null;
          if (data instanceof ICDBaseModel) {
            value1 = data.value.value;
          } else {
            value1 = data.value;
          }

          let value2: string = null;
          if (!this.atdlUtility.isSingleParamOperator(dependency.operator)) {
            value2 = dependency.value;
          }

          if (
            this.evaluateStateFlowApplicability(value1, dependency.operator, value2) &&
            !this.partFillLocked
          ) {
            // visible
            if (
              typeof dependency.action.visible === "boolean" &&
              dependency.action.visible !== this.auxCheckbox.enabled
            ) {
              this.auxCheckbox.enabled = dependency.action.visible;
              this.updateValue();
              this.validate();
              dynResponses.set(ICDNResponseCommand.UPDATE_VALUE, this.data.value);
            }
            // enabled
            if (
              typeof dependency.action.enabled === "boolean" &&
              dependency.action.enabled !== this.auxCheckbox.locked
            ) {
              this.auxCheckbox.locked = !dependency.action.enabled;
              this.updateValue();
              this.validate();
              dynResponses.set(ICDNResponseCommand.UPDATE_VALUE, this.data.value);
            }
            // value change
            if (
              typeof dependency.action.value === "string" &&
              (dependency.action.value === "true" || dependency.action.value === "false")
            ) {
              this.auxCheckbox.value = dependency.action.value === "true";
              if (this.model?.auxiliary?.length === 1) {
                // Stick value back into model so available to validator without needing component access
                let auxControl = this.model.auxiliary[0];
                auxControl.value = this.auxCheckbox.value;
              }
              this.updateValue();
              this.validate();
              dynResponses.set(ICDNResponseCommand.UPDATE_VALUE, this.data.value);
            }
          }
        } catch (ex) {
          logger.out.error(
            `ERROR: ${this.constructor.name}.evaluateAndApplyAuxStateFlowAction(): Exception ${ex}`,
          );
        }
      } else {
        logger.out.error(
          `ERROR: ${this.constructor.name}.evaluateAndApplyAuxStateFlowAction(): ICD [${this.icdId}]: Invalid parameters`,
        );
      }
    }
    return dynResponses;
  }

  protected evaluateStateFlowApplicability(
    data: any,
    operator: ATDLNOperator,
    value2: any,
  ): boolean {
    let applyAction: boolean = false;

    // decompose data
    let value1: any = null;
    if (data instanceof ICDBaseModel) {
      value1 = data.value;
    } else if (data instanceof ICDAuxCheckboxModel) {
      value1 = data.enabled ? data.value : true;
    } else {
      value1 = data;
    }

    // check if dependency condition is true
    switch (operator) {
      case ATDLNOperator.EX: // filed value is required
        applyAction = value1 != null;
        break;
      case ATDLNOperator.NX: // field value should be null
        applyAction = value1 == null;
        break;
      case ATDLNOperator.LE: // filed value is LESS OR EQUAL to secondary value
        applyAction = this.atdlComparator.lessThanEquals(value1, value2);
        break;
      case ATDLNOperator.LT: // filed value is LESS than secondary value
        applyAction = this.atdlComparator.lessThan(value1, value2);
        break;
      case ATDLNOperator.GE: // filed value is GREATER OR EQUAL to secondary value
        applyAction = this.atdlComparator.greaterThanEquals(value1, value2);
        break;
      case ATDLNOperator.GT: // filed value is GREATER then secondary value
        applyAction = this.atdlComparator.greaterThan(value1, value2);
        break;
      case ATDLNOperator.EQ:
        applyAction = this.atdlComparator.equals(value1, value2);
        break;
      case ATDLNOperator.NE:
        applyAction = !this.atdlComparator.equals(value1, value2);
        break;
      default:
        logger.out.error(
          `ERROR: ${this.constructor.name}.evaluateStateFlowApplicability() = Comparison operator '${operator}' not implemented!`,
        );
        applyAction = false;
        break;
    }
    return applyAction;
  }
  /**********************************************************************/
  /* AUX CHECKBOX */
  public onAuxCheckboxChange(): void {
    let dynResponses = new Map();
    this.dirty = true;
    this.cd.markForCheck();
    if (this.auxCheckbox.enabled && !this.auxCheckbox.locked && !this.auxCheckbox.permanentLock) {
      if (this.auxCheckbox.value) {
        this.data.state = ICNInputControlState.ACTIVE;
        this.data.errorMsg = "";
        this.focusInput();
        this.setDefault();
      } else {
        this.data.state = ICNInputControlState.INACTIVE;
        this.data.errorMsg = "";
      }
      if (this.model?.auxiliary?.length === 1) {
        // Stick value back into model so available to validator without needing component access
        let auxControl = this.model.auxiliary[0];
        auxControl.value = this.auxCheckbox.value;
      }
      //
      dynResponses = this.processICDInternalStateFlow(dynResponses);
      //
      this.updateValue();
      this.validate();
      dynResponses.set(ICDNResponseCommand.UPDATE_VALUE, this.data.value);
    }
    this.handleDynResponses(dynResponses);
  }

  /**********************************************************************/
  /* ICD - METHODS  */

  public lock(): void {
    this.locked = true;
    this.cd.markForCheck();
  }

  public unlock(): void {
    this.locked = false;
    this.cd.markForCheck();
  }

  public validate(): boolean {
    if (this.control_validation_state) {
      // dev_test validation control
      if (this.validateIC()) {
        this.applyValidationToModel();
        this.dynRespond(ICDNResponseCommand.PERFORM_VALIDATION, this.data.value);
        return true;
      } else {
        this.applyValidationToModel();
        return false;
      }
    } else {
      return true;
    }
  }

  public applyValidationToModel(): void {
    if (this.model instanceof ATDLInterfaceControlModel) {
      if (this.inputFocused) {
        this.data.state = ICNInputControlState.ACTIVE;
        this.data.errorMsg = ""; // check this out
      } else {
        this.model.validState = this.data.state;
        this.model.errorMsg = this.data.errorMsg;
      }
      this.cd.markForCheck();
    } else {
      logger.out.error(
        `ERROR: ${this.constructor.name}.validateAndApplyToModel(): Invalid model %O`,
        this.model,
      );
    }
  }

  public applyValidationToControl(): void {
    if (this.model instanceof ATDLInterfaceControlModel) {
      if (this.inputFocused) {
        this.data.state = ICNInputControlState.ACTIVE;
        this.data.errorMsg = "";
      } else {
        this.data.state = this.model.validState;
        this.data.errorMsg = this.model.errorMsg;
      }
      this.cd.markForCheck();
    } else {
      logger.out.error(
        `ERROR: ${this.constructor.name}.validateAndApplyToModel(): Invalid model %O`,
        this.model,
      );
    }
  }

  public resetAndReInit(model: ATDLInterfaceControlModel = null): void {
    if (
      model instanceof ATDLInterfaceControlModel ||
      this.model instanceof ATDLInterfaceControlModel
    ) {
      // ID
      this.icdId = model != null ? model.icdId : this.model.icdId;
      // Reset Models ui parameters
      this.model.value = null;
      this.model.validState = ICNInputControlState.INACTIVE;
      this.model.errorMsg = null;
      // Reset
      this.reset(false);
      // Initialize
      this.dynInitializeModel(model != null ? model : this.model);
      // Inform panel
    } else {
      logger.out.error(
        `ERROR: ${this.constructor.name}.resetAndReInit(): Invalid model, operation aborted`,
      );
    }
  }

  public reset(resetInitFlags: boolean = true): void {
    // Main data model
    this.data.value = null;
    this.data.state = ICNInputControlState.INACTIVE;
    this.data.errorMsg = "";
    this.data.disabled = false;
    // Aux model
    this.auxCheckbox.id = null;
    this.auxCheckbox.enabled = false;
    this.auxCheckbox.locked = true;
    this.auxCheckbox.permanentLock = false;
    this.auxCheckbox.value = false;
    this.auxCheckbox.mode = ICDNAuxMode.DISABLED;
    if (this.model?.auxiliary?.length === 1) {
      // Stick value back into model so available to validator without needing component access
      let auxControl = this.model.auxiliary[0];
      auxControl.value = this.auxCheckbox.value;
    }
    // Flags
    this.inputFocused = false;
    this.dirty = false;
    this.required = false;
    //
    this.disabled = false;
    this.visible = true;
    this.locked = false;
    //
    if (resetInitFlags) {
      this.isInitializing = false;
      this.isLoading = false;
    }
    // Reset child specific properties
    this.resetIC();
    // Inform the panel about reset
    this.dynRespond(ICDNResponseCommand.RESET_ACK);
  }

  /**
   * Emit current value without changing - normally called after everything has been initialised to run state rules
   */
  protected pushCurrentValue(): void {
    let dynResponses = new Map();
    if (this.auxCheckbox) {
      if (this.auxCheckbox.value) {
        this.data.state = ICNInputControlState.ACTIVE;
      } else {
        this.data.state = ICNInputControlState.INACTIVE;
      }
    }
    dynResponses = this.processICDInternalStateFlow(dynResponses);
    //
    this.updateValue();
    this.validate();
    dynResponses.set(ICDNResponseCommand.UPDATE_VALUE, this.data.value);
    this.handleDynResponses(dynResponses);
  }
  /**********************************************************************/
  /* ABSTRACT DYN - HANDLERS  */
  protected abstract handleConstraintUpdate(data: any): void;
  protected abstract initializeICDSpecificModelProperties(): boolean;

  /**********************************************************************/
  /* ABSTRACT ICD - METHODS  */
  public abstract focusInput(): void;
  public abstract load(value: T | any, propagate?: boolean): void;
  public abstract validateIC(): boolean;
  public abstract setDefault(): void;
  public abstract resetIC(): void;
  ///
  public abstract updateValue(): void;
  protected abstract setAndUpdateValue(value: any): void;

  /**********************************************************************/
  /* DEBUG - HELPER  */

  protected outputExtendedLogging(value: string, dump: any = null): void {
    this.oeLogger.outputExtendedLogging(value, dump);
  }
}
